import { domainError, type DomainError } from '../../shared/domain-error';
import { normalizeName } from '../../shared/naming';
import { err, ok, type Result } from '../../shared/result';
import type { RowIssue } from '../ganttpro/errors';
import { cleanText, columnOf, readNumber, type Matrix } from '../ganttpro/matrix-cells';
import { PURCHASE_TEMPLATE_ERROR } from './errors';
import { findHeader, headerKey, type RequiredColumn } from './header';
import type { LaborKindData, LaborRowData } from './row-data';

/**
 * Leitura da aba **"Mão de obra"** da planilha de itens de compra (Fase 26, D9).
 *
 * Cada linha é o HH de um item do cronograma — (torre, pavimento, disciplina, serviço,
 * trecho) — aberto por **função**. A planilha traz duas funções: `funcao_cpu`, a da
 * composição do orçamento, e `funcao_cliente_proposta`, a do cadastro do cliente (a folha
 * de pagamento), que o usuário aprovou como de-para (D9). O sistema grava a segunda — é
 * a que existe no cadastro (RN-013) — e a primeira só serve para a mensagem de erro.
 *
 * ## Própria e de terceiros (D9, decisão de 02/10/2026)
 *
 * A coluna opcional `tipo_mao_de_obra` diz se a linha é `PROPRIA` ou `TERCEIROS`. O serviço
 * de terceiros — o pedreiro de acabamento, o marmorista, o pintor, o empreiteiro — não vira
 * função do cadastro: a linha leva a descrição (`descricao_terceiro`, ou a função da CPU
 * quando ela vem vazia) e nenhuma função. Sem a coluna, ou com a célula vazia, a linha é
 * `PROPRIA`, como era antes.
 *
 * O de-para é muitos para um: duas funções da CPU viram a mesma função do cliente, e então
 * o **mesmo (item, função) aparece em mais de uma linha**. O leitor as **soma** — um item
 * tem uma linha por função e uma por serviço de terceiros (RN-351).
 *
 * As colunas `nivel`, `edt_do_item`, `inicio` e `fim` não são lidas: o item se acha pela
 * frente, pelo serviço e pelo trecho (o nível só ordena, e as datas são do item).
 */

/** As grafias aceitas de cada coluna lida, já em chave (`headerKey`). */
export const LABOR_HEADERS = {
  tower: ['torre'],
  floor: ['pavimento', 'andar'],
  discipline: ['disciplina'],
  service: ['servico'],
  segment: ['trecho'],
  cpuRole: ['funcao cpu', 'funcao da cpu'],
  clientRole: ['funcao cliente proposta', 'funcao cliente', 'funcao do cliente', 'funcao proposta'],
  kind: ['tipo mao de obra', 'tipo de mao de obra', 'tipo'],
  thirdParty: [
    'descricao terceiro',
    'descricao do terceiro',
    'descricao terceiros',
    'descricao de terceiros',
  ],
  hours: ['hh', 'hh por funcao', 'hh funcao', 'horas homem'],
} as const;

const REQUIRED: readonly RequiredColumn[] = [
  { label: 'torre', spellings: LABOR_HEADERS.tower },
  { label: 'pavimento', spellings: LABOR_HEADERS.floor },
  { label: 'disciplina', spellings: LABOR_HEADERS.discipline },
  { label: 'servico', spellings: LABOR_HEADERS.service },
  { label: 'funcao_cliente_proposta', spellings: LABOR_HEADERS.clientRole },
  { label: 'hh', spellings: LABOR_HEADERS.hours },
];

const SCAN_LIMIT = 30;

/** Os tamanhos que o contrato (`laborRowSchema`) aceita. */
const LIMITS = {
  tower: 80,
  floor: 80,
  discipline: 160,
  service: 160,
  segment: 60,
  role: 120,
} as const;

/** Como a coluna Serviço escreve a frente inteira (o mesmo texto do modelo da Fase 25). */
const WHOLE_FRONT = new Set(['(frente inteira)', 'frente inteira']);

/** As grafias do tipo, já em chave (`headerKey`, que tira o acento e a caixa). */
const KIND_BY_KEY: ReadonlyMap<string, LaborKindData> = new Map([
  ['propria', 'PROPRIA'],
  ['proprio', 'PROPRIA'],
  ['terceiros', 'TERCEIROS'],
  ['terceiro', 'TERCEIROS'],
  ['terceirizado', 'TERCEIROS'],
]);

export interface LaborReading {
  /** O HH por (item, função) e por (item, terceiro), já com as repetidas somadas (RN-351). */
  readonly rows: LaborRowData[];
  /** Linhas que não puderam ser lidas — as outras seguem. */
  readonly issues: RowIssue[];
  /** Quantas linhas da planilha foram somadas a outra do mesmo (item, tipo, função). */
  readonly unions: number;
  readonly headerRowNumber: number;
}

/** A matriz tem o cabeçalho da aba "Mão de obra"? */
export function looksLikeLabor(matrix: Matrix): boolean {
  return findHeader(matrix, REQUIRED, SCAN_LIMIT).ok;
}

export function readLaborTemplate(matrix: Matrix): Result<LaborReading, DomainError> {
  const search = findHeader(matrix, REQUIRED, SCAN_LIMIT);
  if (!search.ok) {
    const labels = REQUIRED.map((column) => `"${column.label}"`).join(', ');
    return err(
      domainError(
        PURCHASE_TEMPLATE_ERROR.LABOR_HEADER_NOT_FOUND,
        search.nearest === undefined
          ? `Não encontrei o cabeçalho da mão de obra — as colunas ${labels} — nas primeiras ` +
              `${String(SCAN_LIMIT)} linhas desta aba. É a aba "Mão de obra"?`
          : `O cabeçalho da linha ${String(search.nearest.rowNumber)} não tem todas as colunas ` +
              `da mão de obra. Faltam: ${search.nearest.missing
                .map((label) => `"${label}"`)
                .join(', ')}.`,
        search.nearest === undefined ? undefined : { missing: search.nearest.missing },
      ),
    );
  }

  const { header } = search;
  const column = (spellings: readonly string[]) => columnOf(header.columns, spellings);
  const columns = {
    tower: column(LABOR_HEADERS.tower),
    floor: column(LABOR_HEADERS.floor),
    discipline: column(LABOR_HEADERS.discipline),
    service: column(LABOR_HEADERS.service),
    segment: column(LABOR_HEADERS.segment),
    cpuRole: column(LABOR_HEADERS.cpuRole),
    clientRole: column(LABOR_HEADERS.clientRole),
    kind: column(LABOR_HEADERS.kind),
    thirdParty: column(LABOR_HEADERS.thirdParty),
    hours: column(LABOR_HEADERS.hours),
  };

  const issues: RowIssue[] = [];
  const grouped = new Map<string, { row: LaborRowData; hours: number; lines: number }>();
  let unions = 0;

  for (let index = header.rowIndex + 1; index < matrix.length; index += 1) {
    const cells = matrix[index] ?? [];
    const rowNumber = index + 1;
    const at = (position: number | undefined): unknown =>
      position === undefined ? undefined : cells[position];

    const towerName = cleanText(at(columns.tower));
    const floorName = cleanText(at(columns.floor));
    const disciplineName = cleanText(at(columns.discipline));
    const clientRole = cleanText(at(columns.clientRole));
    const cpuRole = cleanText(at(columns.cpuRole));
    const kindText = cleanText(at(columns.kind));
    const hours = readNumber(at(columns.hours));

    // Linha em branco (ou de rodapé): nada nas três colunas que identificam a frente.
    if (towerName === null && floorName === null && disciplineName === null) continue;

    // Sem a coluna, ou com a célula vazia, a linha é a própria — como antes de D9 (02/10).
    const kind: LaborKindData | undefined =
      kindText === null ? 'PROPRIA' : KIND_BY_KEY.get(headerKey(kindText));
    const terceiros = kind === 'TERCEIROS';
    // A função só vale na própria; a descrição, só no terceiro — e, vazia, é a função da CPU.
    const jobRoleName = terceiros ? null : clientRole;
    const thirdPartyDescription = terceiros ? (cleanText(at(columns.thirdParty)) ?? cpuRole) : null;
    const who = terceiros ? thirdPartyDescription : jobRoleName;

    const serviceText = cleanText(at(columns.service));
    const serviceName =
      serviceText === null || WHOLE_FRONT.has(normalizeName(serviceText)) ? null : serviceText;
    const segment = cleanText(at(columns.segment));
    const label = [floorName, disciplineName, serviceName, who ?? cpuRole]
      .filter((part) => part !== null)
      .join(' / ');

    if (kind === undefined) {
      issues.push({
        rowNumber,
        message: `${label === '' ? 'Linha' : label}: o tipo de mão de obra "${String(kindText)}" não é PROPRIA nem TERCEIROS.`,
      });
      continue;
    }

    const absent = [
      ['Torre', towerName],
      ['Pavimento', floorName],
      ['Disciplina', disciplineName],
      terceiros
        ? ['Descrição do terceiro (descricao_terceiro)', who]
        : ['Função do cliente (funcao_cliente_proposta)', who],
    ]
      .filter(([, value]) => value === null)
      .map(([name]) => name);
    if (
      absent.length > 0 ||
      towerName === null ||
      floorName === null ||
      disciplineName === null ||
      who === null
    ) {
      issues.push({
        rowNumber,
        message:
          `${label === '' ? 'Linha' : label}: faltam ${absent.join(', ')}` +
          (!terceiros && who === null && cpuRole !== null ? ` (a CPU diz "${cpuRole}")` : '') +
          '.',
      });
      continue;
    }

    const problems: string[] = [];
    let hoursValue: number | undefined;
    if (!hours.ok) {
      problems.push(`o HH "${String(at(columns.hours))}" não é um número`);
    } else if (hours.value === null) {
      problems.push('o HH está vazio');
    } else if (hours.value < 0) {
      problems.push(`o HH ${String(hours.value)} é negativo`);
    } else {
      hoursValue = hours.value;
    }
    for (const [name, text, max] of [
      ['A torre', towerName, LIMITS.tower],
      ['O pavimento', floorName, LIMITS.floor],
      ['A disciplina', disciplineName, LIMITS.discipline],
      ['O serviço', serviceName, LIMITS.service],
      ['O trecho', segment, LIMITS.segment],
      [terceiros ? 'A descrição do terceiro' : 'A função', who, LIMITS.role],
    ] as const) {
      if (text !== null && text.length > max) {
        problems.push(`${name.toLowerCase()} tem mais de ${String(max)} caracteres`);
      }
    }
    if (problems.length > 0 || hoursValue === undefined) {
      issues.push({ rowNumber, message: `${label}: ${problems.join('; ')}.` });
      continue;
    }

    // RN-351: um item tem uma linha por função e uma por terceiro. O item é (frente,
    // serviço, trecho); o tipo separa a função "Pintor" do terceiro "Pintor".
    const key = [towerName, floorName, disciplineName, serviceName ?? '', segment ?? '', kind, who]
      .map(normalizeName)
      .join('\u0001');
    const group = grouped.get(key);
    if (group === undefined) {
      grouped.set(key, {
        row: {
          rowNumber,
          towerName,
          floorName,
          disciplineName,
          serviceName,
          segment,
          kind,
          jobRoleName,
          thirdPartyDescription,
          hours: hoursValue,
        },
        hours: hoursValue,
        lines: 1,
      });
    } else {
      group.hours += hoursValue;
      group.lines += 1;
      unions += 1;
    }
  }

  const rows = [...grouped.values()].map((group) =>
    group.lines === 1 ? group.row : { ...group.row, hours: round(group.hours) },
  );
  if (rows.length === 0 && issues.length === 0) {
    return err(
      domainError(
        PURCHASE_TEMPLATE_ERROR.LABOR_NO_ROWS,
        'A aba "Mão de obra" tem o cabeçalho, mas nenhuma linha de HH abaixo dele.',
      ),
    );
  }

  return ok({ rows, issues, unions, headerRowNumber: header.rowIndex + 1 });
}

/** A soma de decimais acumula resíduo (0,1 + 0,2): arredonda ao milionésimo. */
function round(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}
